import { splitUuid } from '@airreading/common';
import { CopyButton } from './copy-button';
import { cn } from '@/lib/tailwind-utils';

export interface AlphanumericCodeProps {
  code: string;
  chunkSize: number;
  lastChunkSize?: number;
}

export function AlphanumericCode({ code: rawCode, chunkSize, lastChunkSize = chunkSize }: AlphanumericCodeProps) {
  const code = rawCode.trim();
  const length = code.length;
  const lastChunkIndex = length - lastChunkSize;

  return (
    <pre className="inline-block font-mono divide-x-2">
      {[
        ...(function* () {
          for (let i = 0, isOdd = true; i < lastChunkIndex; i += chunkSize, isOdd = !isOdd) {
            const endIndex = i + chunkSize > lastChunkIndex ? lastChunkIndex : i + chunkSize;
            const segment = code.substring(i, endIndex);
            yield (
              <span key={i} className={isOdd ? 'text-air-dark-blue' : ''}>
                {segment}
              </span>
            );
          }

          yield (
            <span key="last" className="text-air-orange">
              {code.substring(lastChunkIndex)}
            </span>
          );
        })(),
      ]}
    </pre>
  );
}

export interface OpaqueCodeProps {
  code: string | null;
}

export function OpaqueCode({ code }: OpaqueCodeProps) {
  return (
    code && (
      <>
        <pre className="inline text-wrap font-mono">{code}</pre>
        <CopyButton content={code} />
      </>
    )
  );
}

export interface EdlinkIdCodeProps {
  code: string | null;
  showCopyButton?: boolean;
  showColor?: boolean;
}

export function EdlinkIdCode({ code, showCopyButton = true, showColor = true }: EdlinkIdCodeProps) {
  if (!code) {
    return null;
  }

  let content: React.ReactNode = code;
  const LAST_CHUNK_SIZE = 6;

  try {
    const { timeLow, timeMid, version, variant, node } = splitUuid(code);
    const nodePrefix = node.slice(0, Math.max(0, node.length - LAST_CHUNK_SIZE));
    const nodeSuffix = node.slice(-LAST_CHUNK_SIZE);

    content = (
      <>
        <pre className={cn('inline text-wrap font-mono', showColor ? 'text-air-dark-blue' : '')}>
          {timeLow}-<span className={cn(showColor ? 'text-air-orange' : '')}>{timeMid}</span>-{version}-{variant}-
          {nodePrefix}
          <span className={cn(showColor ? 'text-air-orange' : '')}>{nodeSuffix}</span>
        </pre>
        {showCopyButton && <CopyButton content={code} />}
      </>
    );
  } catch {
    content = <OpaqueCode code={code} />;
  }

  return content;
}
